import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Clock, Camera, ScanFace, IdCard, Lock } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner, VerifiedBadge } from "../components/common";

export default function KYC() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(user?.identity_verified ? "verified" : (user?.verification_status || "unverified"));
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/kyc/identity/status");
      const vs = data.identity_verified ? "verified" : (data.status || data.verification_status);
      setStatus(vs);
      if (data.identity_verified) await refreshUser();
      return vs;
    } catch (e) { return null; }
  }, [refreshUser]);

  useEffect(() => { refresh(); }, [refresh]);

  // After returning from Stripe hosted verification (?identity=done), poll until resolved.
  useEffect(() => {
    if (params.get("identity") !== "done") return;
    setChecking(true);
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      const vs = await refresh();
      if (vs === "verified" || tries >= 20) {
        clearInterval(id);
        setChecking(false);
        if (vs === "verified") toast.success("Identity verified — you now have the blue check");
        setParams({}, { replace: true });
      }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startVerification = async () => {
    if (starting) return;
    setStarting(true); setSetupRequired(false);
    try {
      const { data } = await api.post("/kyc/identity/session");
      if (data.already_verified) { await refresh(); return; }
      if (data.url) { window.location.href = data.url; return; }
      toast.error("Could not start verification. Please try again.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "SETUP_REQUIRED") setSetupRequired(true);
      else toast.error(errMsg(e));
    } finally { setStarting(false); }
  };

  const verified = status === "verified";
  const pending = status === "pending" || status === "processing" || checking;

  return (
    <div className="animate-fade-up" data-testid="kyc-page">
      <PageHeader title="Blue check verification" subtitle="Verify your identity with a live camera check" />

      {/* Hero */}
      <div className="rounded-3xl p-6 text-white mb-5 shadow-[0_18px_44px_rgba(75,35,204,0.3)]"
        style={{ background: "linear-gradient(135deg,#4B23CC,#7C3AED)" }}>
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/15"><ScanFace size={24} /></span>
          <div>
            <div className="font-display text-lg font-semibold flex items-center gap-1.5">
              Get the blue check {verified && <VerifiedBadge color="blue" size={18} testid="kyc-blue-seal" />}
            </div>
            <div className="text-white/70 text-xs">Live selfie + government ID · powered by Stripe</div>
          </div>
        </div>
      </div>

      {verified ? (
        <div data-testid="kyc-verified-banner" className="rounded-2xl border border-success/20 bg-success/10 text-success p-4 flex items-start gap-3">
          <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Identity verified</div>
            <div className="text-sm opacity-90">You have the blue verified check. You can buy, sell and send crypto, and send money worldwide.</div>
          </div>
        </div>
      ) : pending ? (
        <div data-testid="kyc-pending-banner" className="rounded-2xl border border-warning/30 bg-warning/10 text-warning p-4 flex items-start gap-3">
          <Clock size={20} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Checking your verification…</div>
            <div className="text-sm opacity-90">This can take a few moments after you finish. You can leave and come back — we'll update automatically.</div>
          </div>
        </div>
      ) : setupRequired ? (
        <div data-testid="kyc-setup-banner" className="rounded-2xl border border-border bg-card p-4">
          <div className="font-semibold">SETUP REQUIRED</div>
          <div className="text-sm text-muted-foreground mt-1">Live identity verification isn't available on this account yet. No verification was started.</div>
        </div>
      ) : (
        <>
          <div className="rounded-3xl bg-card border border-border p-5 space-y-4">
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><h2 className="font-display font-semibold">How it works</h2></div>
            <Step icon={IdCard} title="Scan your ID" body="Use your phone camera to capture your passport, driver's license or ID card." />
            <Step icon={Camera} title="Take a live selfie" body="A quick live selfie confirms it's really you. Camera only — no photo uploads." />
            <Step icon={CheckCircle2} title="Get your blue check" body="Once Stripe confirms your identity, your blue check is added automatically." />
            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
              <Lock size={13} /> Your documents are handled securely by Stripe. Jack Cash never stores your ID images.
            </div>
          </div>

          <button data-testid="kyc-start-btn" onClick={startVerification} disabled={starting}
            className="w-full h-14 mt-5 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(75,35,204,0.3)] disabled:opacity-60">
            {starting ? <Spinner className="w-5 h-5" /> : <><ScanFace size={18} /> Start verification</>}
          </button>
        </>
      )}
    </div>
  );
}

function Step({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary/10 text-primary shrink-0"><Icon size={17} /></span>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{body}</div>
      </div>
    </div>
  );
}
